
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BirdieRecord, getManualPlayerId, toManualUserId } from "@/types";
import { getScores } from "@/integrations/supabase/scores";
import { supabase } from "@/integrations/supabase/client";

export const queryKeys = {
  birdies: "birdies",
  userBirdies: (userIds: string[], includeSample: boolean) =>
    ["birdies", "users", userIds.sort().join(','), includeSample ? "withSample" : "noSample"],
};

/**
 * Fetch birdie records for specific users, excluding scores
 * linked to sample games (is_sample = true) unless includeSampleData is true.
 */
export const useBirdieRecordsQuery = (userIds: string[] = [], includeSampleData = false) => {
  return useQuery({
    queryKey: queryKeys.userBirdies(userIds, includeSampleData),
    queryFn: async () => {
      if (!userIds.length) return [];

      try {
        // 1. Get sample score IDs to exclude (bypasses games RLS)
        //    include_own=false: exclude ALL sample scores (toggle OFF)
        //    include_own=true:  keep MY sample game scores, exclude others' (toggle ON)
        const { data: sampleIds } = await supabase.rpc('get_sample_score_ids', {
          include_own: includeSampleData,
        });
        const sampleScoreIds = new Set<string>(sampleIds || []);

        // 2. Split IDs into real user IDs and manual player raw IDs
        const realIds: string[] = [];
        const manualRawIds: string[] = [];
        for (const uid of userIds) {
          const mpId = getManualPlayerId(uid);
          if (mpId) {
            manualRawIds.push(mpId);
          } else {
            realIds.push(uid);
          }
        }

        const records: BirdieRecord[] = [];

        // 3a. Fetch scores for real users (excluding rows with manual_player_id to prevent double-counting)
        if (realIds.length > 0) {
          const realScores = await getScores({ userIds: realIds });
          for (const score of realScores) {
            if (sampleScoreIds.has(score.id)) continue;
            if (score.manual_player_id) continue; // belongs to a manual player, not the real user
            records.push({
              id: score.id,
              userId: score.user_id,
              courseId: score.course_id,
              holeNumber: score.hole_number,
              date: score.score_date,
              teeBox: score.tee,
              scoreType: score.score_type,
              notes: score.notes || undefined,
              gameIds: []
            });
          }
        }

        // 3b. Fetch scores for manual players
        if (manualRawIds.length > 0) {
          const manualScores = await getScores({ manualPlayerIds: manualRawIds });
          for (const score of manualScores) {
            if (sampleScoreIds.has(score.id)) continue;
            records.push({
              id: score.id,
              userId: toManualUserId(score.manual_player_id!),
              courseId: score.course_id,
              holeNumber: score.hole_number,
              date: score.score_date,
              teeBox: score.tee,
              scoreType: score.score_type,
              notes: score.notes || undefined,
              gameIds: []
            });
          }
        }

        return records;
      } catch (error) {
        console.error("Error fetching birdie records:", error);
        return [];
      }
    },
    enabled: userIds.length > 0,
    staleTime: 60 * 60 * 1000, // 60 minutes
    gcTime: 120 * 60 * 1000, // 120 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

/**
 * Invalidate birdie queries to force a refresh
 */
export const useInvalidateBirdieQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAllBirdies: () => {
      return queryClient.invalidateQueries({
        queryKey: [queryKeys.birdies]
      });
    }
  };
};
